import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  CatalogPlan,
  CatalogResponse,
  normalizeCatalog,
} from '@/client/types/promotion-pricing/catalog';
import { DEFAULT_CCODE } from '@/client/types/promotion-pricing';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const tradeableCatalogKeys = {
  all: ['tradeable-catalog'] as const,
  lists: () => [...tradeableCatalogKeys.all, 'list'] as const,
  list: (ccode: string) => [...tradeableCatalogKeys.lists(), ccode] as const,
};

// ─── Full tradeable-object catalog ─────────────────────────────────────────────
// The endpoint ignores query params (it filters from a JSON body) and always
// outer-joins Price, so a plan with N prices comes back as N duplicate rows with
// a singular `price` each. We fetch everything, dedupe by `toid`, and let the UI
// group/filter client-side. `ccode` is a client-side scope only (see query key).

const useTradeableCatalogFn = () => {
  const request = useClientStore((s) => s.request);

  return withErrorHandling(
    async (): Promise<CatalogPlan[]> => {
      const response = await request<CatalogResponse>('/api/admin/tradeable_objects/');
      return normalizeCatalog(response.data?.tradeable_objects ?? []);
    },
    'An unexpected error occurred while fetching the catalog',
  );
};

export const useTradeableCatalogQuery = (ccode: string = DEFAULT_CCODE) => {
  const queryFn = useTradeableCatalogFn();
  return useQuery({
    queryKey: tradeableCatalogKeys.list(ccode),
    queryFn: () => queryFn(),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
